// ===========================================
// Revenue Summary Cards Component
// ===========================================
// Display key revenue metrics using StatWidget cards

import { Grid } from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  AccountBalance as BalanceIcon,
  Warning as WarningIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';

import { RevenueReportSummary } from '../../api/reports.api';
import { StatWidget } from '../dashboard/StatWidget';
import { formatCurrency } from '../../hooks/useReports';

// ===========================================
// TYPES
// ===========================================

interface RevenueSummaryCardsProps {
  summary: RevenueReportSummary;
  loading?: boolean;
}

// ===========================================
// COMPONENT
// ===========================================

export default function RevenueSummaryCards({
  summary,
  loading = false,
}: RevenueSummaryCardsProps) {
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {/* Total Invoiced - Primary metric */}
      <Grid item xs={12} sm={6} md={2.4}>
        <StatWidget
          title="Total Revenue"
          value={formatCurrency(summary.totalRevenue)}
          icon={<MoneyIcon />}
          color="primary"
          subtitle={`${summary.invoiceCount} invoices`}
          loading={loading}
        />
      </Grid>

      {/* Total Collected */}
      <Grid item xs={12} sm={6} md={2.4}>
        <StatWidget
          title="Total Collected"
          value={formatCurrency(summary.totalCollected)}
          icon={<BalanceIcon />}
          color="success"
          subtitle={
            summary.totalRevenue > 0
              ? `${((summary.totalCollected / summary.totalRevenue) * 100).toFixed(1)}% collected`
              : '0% collected'
          }
          loading={loading}
        />
      </Grid>

      {/* Outstanding */}
      <Grid item xs={12} sm={6} md={2.4}>
        <StatWidget
          title="Outstanding"
          value={formatCurrency(summary.totalOutstanding)}
          icon={<WarningIcon />}
          color="error"
          loading={loading}
        />
      </Grid>

      {/* Average Invoice Value */}
      <Grid item xs={12} sm={6} md={2.4}>
        <StatWidget
          title="Avg Invoice"
          value={formatCurrency(summary.averageInvoiceValue)}
          icon={<ReceiptIcon />}
          color="info"
          loading={loading}
        />
      </Grid>

      {/* Invoice Count */}
      <Grid item xs={12} sm={6} md={2.4}>
        <StatWidget
          title="Total Invoices"
          value={summary.invoiceCount}
          icon={<TrendingUpIcon />}
          color="secondary"
          loading={loading}
        />
      </Grid>
    </Grid>
  );
}
